import axios from 'axios';
import * as Constants from '../common/constants';
import * as HelperFunctions from '../common/functions/helperFunctions';
import { UserModel } from '../models/user/userModel';
import { UserChangePasswordModel } from '../models/user/userChangePasswordModel';
import { UserDiseaseModel } from '../models/user/userDiseaseModel';
import { UserMedicineModel } from '../models/user/userMedicineModel';
import { UserPrescriptionModel } from '../models/user/userPrescriptionModel';
import { UserMedicineToUserDiseaseModel } from '../models/user/userMedicineToUserDiseaseModel';
import AuthService from './authService';
import { UserForGridModel } from '../models/user/userForGridModel';
import { UserMedicineHistoryModel } from '../models/user/userMedicineHistoryModel';
import { UserNotificationModel } from '../models/user/userNotificationModel';
import { UserDiseaseHistoryModel } from '../models/user/userDiseaseHistoryModel';
import { UserMedicineWithHistoryWrapper } from '../models/user/userMedicineWithHistoryWrapper';
import { UserDiseaseWithHistoryModel } from '../models/user/userDiseaseWithHistoryModel';
import { UserMedicineSettingsModel } from '../models/user/userMedicineSettingsModel';
import { UserDiseaseSettingsModel } from '../models/user/userDiseaseSettingsModel';
class UserService {

  lang: string = '';
  authService = new AuthService();

  constructor(lang?) {
    this.lang == lang;
    axios.defaults.headers = {
      "Content-Type": "application/json; charset=utf-8",
      Authorization: `Bearer ${this.authService.getToken()}`,
      Pragma: 'no-cache',
      "langId": HelperFunctions.getLangId(lang)
    }
  }

  getUser() {
    return axios({
      url: `${Constants.apiUrl}/user/getById`,
      method: 'get'
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  updateUser(user: UserModel) {
    return axios({
      url: `${Constants.apiUrl}/user/update`,
      method: 'put',
      data: JSON.stringify(user)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  deleteAccountPermanently() {
    return axios({
      url: `${Constants.apiUrl}/user/deleteAccountPermanently/`,
      method: 'delete'
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getAccountInfo(lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/getAccountInfo/`,
      method: 'get',
      headers: {
        "langId": HelperFunctions.getLangId(lang)
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  changePassword(user: UserChangePasswordModel) {
    return axios({
      url: `${Constants.apiUrl}/user/changePassword/`,
      method: 'put',
      data: JSON.stringify(user)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  //User diseases
  getAllUserDiseases() {
    return axios({
      url: `${Constants.apiUrl}/user/userDiseases/`,
      method: 'get'
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getAllUserDiseasesWithHistoryCount(userId: number, from: number, numberOfRows: number, lang: string, settings: UserDiseaseSettingsModel) {
    return axios({
      url: `${Constants.apiUrl}/user/userDiseasesWithHistoryCount/${userId}/${from}/${numberOfRows}`,
      method: 'post',
      headers: {
        "langId": HelperFunctions.getLangId(lang)
      },
      data: JSON.stringify(settings)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getUserDisease(userId: number, diseaseId: number, lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/userDisease/${userId}/${diseaseId}`,
      method: 'get',
      headers: {
        "langId": HelperFunctions.getLangId(lang)
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  addUserDisease(userDisease: UserDiseaseWithHistoryModel) {
    return axios({
      url: `${Constants.apiUrl}/user/addUserDisease/`,
      method: 'post',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userDisease)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  updateUserDisease(userDisease: UserDiseaseModel) {
    return axios({
      url: `${Constants.apiUrl}/user/updateUserDisease/`,
      method: 'put',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userDisease)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  deleteUserDisease(userId: number, diseaseId: number, historyId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/deleteUserDisease/${userId}/${diseaseId}/${historyId}`,
      method: 'delete'
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  isDiseaseInserted(userId: number, diseaseId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/isDiseaseInserted/${userId}/${diseaseId}`,
      method: 'get'
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  addDiseaseHistory(userId: number, diseaseId: number, userDiseaseHistoryModel: UserDiseaseHistoryModel) {
    return axios({
      url: `${Constants.apiUrl}/user/addDiseaseHistory/${userId}/${diseaseId}`,
      method: 'post',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userDiseaseHistoryModel)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  updateDiseaseHistory(userDiseaseHistoryModel: UserDiseaseHistoryModel) {
    return axios({
      url: `${Constants.apiUrl}/user/updateDiseaseHistory/`,
      method: 'put',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userDiseaseHistoryModel)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  deleteDiseaseHistory(userDiseaseHistoryId) {
    return axios({
      url: `${Constants.apiUrl}/user/deleteDiseaseHistory/${userDiseaseHistoryId}`,
      method: 'delete'
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }


  //User medicines
  getAllUserMedicines() {
    return axios({
      url: `${Constants.apiUrl}/user/userMedicines/`,
      method: 'get'
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getUserMedicine(userId: number, medicineId: number, lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/userMedicine/${userId}/${medicineId}`,
      method: 'get',
      headers: {
        "langId": HelperFunctions.getLangId(lang)
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getAllUserMedicinesWithHistory(userId: number, from: number, numberOfRows: number, lang: string, medSettings: UserMedicineSettingsModel) {
    return axios({
      url: `${Constants.apiUrl}/user/userMedicinesWithHistory/${userId}/${from}/${numberOfRows}`,
      method: 'post',
      headers: {
        "langId": HelperFunctions.getLangId(lang)
      },
      data: JSON.stringify(medSettings)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  addUserMedicine(userMedicine: UserMedicineWithHistoryWrapper) {
    return axios({
      url: `${Constants.apiUrl}/user/addUserMedicine/`,
      method: 'post',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userMedicine)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  updateUserMedicine(userMedicine: UserMedicineModel) {
    return axios({
      url: `${Constants.apiUrl}/user/updateUserMedicine/`,
      method: 'put',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userMedicine)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  deleteUserMedicine(userId: number, medicineId: number, medHistoryId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/deleteUserMedicine/${userId}/${medicineId}/${medHistoryId}`,
      method: 'delete'
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  addMedicineHistory(userId: number, medicineId: number, userMedicineHistoryModel: UserMedicineHistoryModel) {
    return axios({
      url: `${Constants.apiUrl}/user/addUserMedicineHistory/${userId}/${medicineId}`,
      method: 'post',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userMedicineHistoryModel)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  updateMedicineHistory(userMedicineHistoryModel: UserMedicineHistoryModel) {
    return axios({
      url: `${Constants.apiUrl}/user/updateMedicineHistory`,
      method: 'put',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userMedicineHistoryModel)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  deleteMedicineHistory(userMedicineHistoryId) {
    return axios({
      url: `${Constants.apiUrl}/user/deleteMedicineHistory/${userMedicineHistoryId}`,
      method: 'delete',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  isMedicineInserted(userId: number, medicineId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/isMedicineInserted/${userId}/${medicineId}`,
      method: 'get',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  // User Family
  getUserFamilyDropdownList() {
    return axios({
      url: `${Constants.apiUrl}/user/myFamily/getDropdownList/`,
      method: 'get',
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getMyFamily() {
    return axios({
      url: `${Constants.apiUrl}/user/myFamily/getAll`,
      method: 'get'
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getUserFromMyFamily(childUserId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/myFamily/get/${childUserId}`,
      method: 'get'
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  deleteUserFromFamily(childUserId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/myFamily/delete/${childUserId}`,
      method: 'delete'
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  deleteCaretaker(childUserId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/myFamily/deleteCaretaker/${childUserId}`,
      method: 'delete'
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  addMemberInMyFamily(user: UserForGridModel) {
    return axios({
      url: `${Constants.apiUrl}/user/myFamily/create`,
      method: 'post',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(user)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  addCaretaker(user: UserForGridModel) {
    return axios({
      url: `${Constants.apiUrl}/user/myFamily/addCaretaker`,
      method: 'post',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(user)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  updateMyMemberFromFamily(user: UserForGridModel) {
    return axios({
      url: `${Constants.apiUrl}/user/myFamily/update`,
      method: 'put',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(user)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }


  //user prescriptions
  getAllUserPrescriptions(from: number, numberOfRows: number, lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/userPrescriptions/${from}/${numberOfRows}/`,
      method: 'get',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache',
        "langId": HelperFunctions.getLangId(lang)
      },
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getUserPrescription(userPrescriptionId: number, lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/userPrescription/${userPrescriptionId}`,
      method: 'get',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache',
        "langId": HelperFunctions.getLangId(lang)
      },
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getUserPrescriptionFromLink(userPrescriptionId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/prescription/${userPrescriptionId}`,
      method: 'get',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  addUserPrescription(userPrescription: UserPrescriptionModel) {
    return axios({
      url: `${Constants.apiUrl}/user/addUserPrescription/`,
      method: 'post',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userPrescription)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  updateUserPrescription(userPrescription: UserPrescriptionModel) {
    return axios({
      url: `${Constants.apiUrl}/user/updateUserPrescription/`,
      method: 'put',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userPrescription)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  deleteUserPrescription(userPrescriptionId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/deleteUserPrescription/${userPrescriptionId}`,
      method: 'delete',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getPrescriptionMedicines(userPrescriptionId: number, lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/getPrescriptionMedicines/${userPrescriptionId}`,
      method: 'get',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache',
        "langId": HelperFunctions.getLangId(lang)
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  addPrescriptionMedicine(userPrescriptionId: number, userMedicine: UserMedicineHistoryModel) {
    return axios({
      url: `${Constants.apiUrl}/user/addPrescriptionMedicine/${userPrescriptionId}`,
      method: 'post',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userMedicine)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  updatePrescriptionMedicine(userPrescriptionId: number, userMedicine: UserMedicineHistoryModel) {
    return axios({
      url: `${Constants.apiUrl}/user/updatePrescriptionMedicine/${userPrescriptionId}`,
      method: 'put',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userMedicine)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  deletePrescriptionMedicine(userPrescriptionId: number, userMedicineHistoryId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/deletePrescriptionMedicine/${userPrescriptionId}/${userMedicineHistoryId}`,
      method: 'delete',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getHistoryNotifications(userMedicineHistoryId: number, lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/getMedicineHistoryNotifications/${userMedicineHistoryId}`,
      method: 'get',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache',
        "langId": HelperFunctions.getLangId(lang)
      },
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getMedicineNotifications(userMedicineId: number, lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/getMedicineNotifications/${userMedicineId}`,
      method: 'get',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache',
        "langId": HelperFunctions.getLangId(lang)
      },
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  addNotification(userNotification: UserNotificationModel, lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/addNotification/`,
      method: 'post',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache',
        "langId": HelperFunctions.getLangId(lang)
      },
      data: JSON.stringify(userNotification)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  updateNotification(userNotificationId: number, isActive: boolean) {
    return axios({
      url: `${Constants.apiUrl}/user/updateNotification/${userNotificationId}/${isActive}`,
      method: 'put',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  deleteNotification(userNotificationId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/deleteNotification/${userNotificationId}`,
      method: 'delete',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getPrescriptionDiseases(userPrescriptionId: number, lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/getPrescriptionDiseases/${userPrescriptionId}`,
      method: 'get',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache',
        "langId": HelperFunctions.getLangId(lang)
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  addPrescriptionDisease(userPrescriptionId: number, userDisease: UserDiseaseHistoryModel) {
    return axios({
      url: `${Constants.apiUrl}/user/addPrescriptionDisease/${userPrescriptionId}`,
      method: 'post',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userDisease)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  updatePrescriptionDisease(userPrescriptionId: number, userDisease: UserDiseaseHistoryModel) {
    return axios({
      url: `${Constants.apiUrl}/user/updatePrescriptionDisease/${userPrescriptionId}`,
      method: 'put',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(userDisease)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  deletePrescriptionDisease(userPrescriptionId: number, userDiseaseId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/deletePrescriptionDisease/${userPrescriptionId}/${userDiseaseId}`,
      method: 'delete',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  // Push subscriptions
  createPushSubscription(subscription) {
    return axios({
      url: `${Constants.apiUrl}/user/createSubscription/`,
      method: 'post',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
      data: JSON.stringify(subscription)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  discardPushSubscription(subscription: string) {
    return axios({
      url: `${Constants.apiUrl}/user/discardSubscription/${subscription}`,
      method: 'delete',
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${this.authService.getToken()}`,
        Pragma: 'no-cache'
      },
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getAllUserNotifications(lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/getUserNotifications/`,
      method: 'get',
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`,
        "langId": HelperFunctions.getLangId(lang)
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getNotSeenNotifications(lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/getNotSeenNotifications/`,
      method: 'get',
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`,
        "langId": HelperFunctions.getLangId(lang)
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  markAllAsSeen() {
    return axios({
      url: `${Constants.apiUrl}/user/markAllAsSeen/`,
      method: 'get',
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getNotificationScheduler(schedulerId: number) {
    return axios({
      url: `${Constants.apiUrl}/user/getNotificationScheduler/${schedulerId}/`,
      method: 'get',
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  setTakenNotification(schedulerId: number, acceptanceType: number, acceptanceDate: string) {
    return axios({
      url: `${Constants.apiUrl}/user/setTakenNotification/${schedulerId}/${acceptanceType}/`,
      method: 'post',
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`
      },
      data: JSON.stringify(acceptanceDate)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  setSnoozedNotification(schedulerId: number, snoozeMinutes: number, snoozeDate: string) {
    return axios({
      url: `${Constants.apiUrl}/user/setSnoozedNotification/${schedulerId}/${snoozeMinutes}/`,
      method: 'post',
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`
      },
      data: JSON.stringify(snoozeDate)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getAllNotifications(first: number, rows: number, lang: string) {
    return axios({
      url: `${Constants.apiUrl}/user/getAllNotifications/${first}/${rows}`,
      method: 'get',
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`,
        "langId": HelperFunctions.getLangId(lang)
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getAllCaretakers() {
    return axios({
      url: `${Constants.apiUrl}/user/getAllCaretakers/`,
      method: 'get',
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  getFilteredUsers(from: number, numberOfRows: number, email: string) {
    return axios({
      url: `${Constants.apiUrl}/user/getFilteredUsers/${from}/${numberOfRows}/${email}`,
      method: 'get',
      headers: {
        Authorization: `Bearer ${this.authService.getToken()}`
      }
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }

  validateCaretaker(confirmCode: string) {
    return axios({
      url: `${Constants.apiUrl}/user/validateCaretaker/`,
      method: 'post',
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.authService.getToken()}`
      },
      data: JSON.stringify(confirmCode)
    })
      .then(res => res.data.data)
      .catch(error =>
        Promise.reject(HelperFunctions.getErrorMessage(error))
      )
  }
}


export default UserService;